import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Users, Phone, Crown, Search, Trash2, IdCard, Sparkles, MapPin, Store } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";
import CustomerLocationsModal from "./CustomerLocationsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// Customer tier metadata — A_plus (regular) sees the normal price; B_plus
// (special/VIP) sees wholesale prices on products that have them set.
const TIER_META = {
  A_plus: { label: "A+", description: "زبون عادي", color: "#3B82F6", bg: "#E0F2FE" },
  B_plus: { label: "B+", description: "زبون مميّز",  color: "#7C3AED", bg: "#F5F3FF" },
};

const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [locOpen, setLocOpen] = useState(null); // customer id whose locations modal is open

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customers`);
      setCustomers(data);
    } catch {
      toast.error("فشل تحميل قائمة الزبائن");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (cust) => {
    const ok = await confirmDialog(
      `⚠️ تحذير!\nسيتم حذف الزبون "${cust.market_name || cust.full_name}" وجميع طلباته وطلبات سحبه بشكل نهائي.\n\nهل أنت متأكد؟`
    );
    if (!ok) return;
    setDeletingId(cust.id);
    try {
      const { data } = await axios.delete(`${API}/admin/customers/${cust.id}`);
      toast.success(
        `تم حذف الزبون (${data.orders_deleted} طلب، ${data.withdrawals_deleted} طلب سحب)`
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل حذف الزبون");
    } finally {
      setDeletingId(null);
    }
  };

  // Toggle a customer between A_plus and B_plus. The backend records the
  // change in activity_logs and rejects any other value.
  const handleTierToggle = async (cust) => {
    const currentTier = cust.customer_tier || "A_plus";
    const newTier = currentTier === "A_plus" ? "B_plus" : "A_plus";
    try {
      await axios.patch(`${API}/admin/customers/${cust.id}/tier`, { tier: newTier });
      toast.success(`تم تغيير الزبون إلى ${TIER_META[newTier].label} (${TIER_META[newTier].description})`);
      // Optimistic in-place update so the badge flips instantly without refetch
      setCustomers((prev) => prev.map((c) => c.id === cust.id ? { ...c, customer_tier: newTier } : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تغيير صنف الزبون");
    }
  };

  // Toggle "يمكن الشراء منه" — makes this customer selectable as a supplier
  // in purchase invoices. Two-way accounting: buying from them reduces their
  // debt (or creates negative debt = we owe them).
  const handleBuyFromToggle = async (cust) => {
    const next = !cust.can_buy_from;
    try {
      await axios.patch(`${API}/admin/customers/${cust.id}/can-buy-from`, { enabled: next });
      toast.success(next
        ? "✓ تم تفعيل الشراء من هذا الزبون — راح يظهر بقائمة المُجهّزين"
        : "✗ تم إيقاف الشراء من هذا الزبون");
      setCustomers((prev) => prev.map((c) => c.id === cust.id ? { ...c, can_buy_from: next } : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشلت العملية");
    }
  };

  const filtered = customers.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.full_name?.toLowerCase().includes(q) ||
      c.market_name?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      (c.customer_id || "").toLowerCase().includes(q) ||
      c.location?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>;
  }

  return (
    <div data-testid="customers-tab" className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <Users className="w-6 h-6 text-[#2D4A3E]" />
          إدارة الزبائن
          <span className="text-sm text-[#5C6E64] font-normal">({customers.length} زبون)</span>
        </h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute right-3 top-2.5 text-[#5C6E64]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم / الماركت / الموبايل / رقم الايباد"
            data-testid="cust-search"
            className="pl-4 pr-10 py-2 rounded-full border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-sm w-72"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cust-list">
        {filtered.map((c, idx) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.02 }}
            data-testid={`cust-${c.id}`}
            className="bg-white border border-[#E4EBE6] rounded-2xl p-4 space-y-3 relative"
          >
            {idx < 3 && (
              <span className="absolute -top-2 -right-2 bg-gradient-to-br from-[#F5A623] to-[#E85D45] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shadow-md">
                <Crown className="w-3.5 h-3.5" />
              </span>
            )}
            <div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#111814] truncate">{c.market_name || c.full_name}</p>
                  <p className="text-xs text-[#5C6E64] truncate">{c.full_name}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {(() => {
                    const tier = c.customer_tier || "A_plus";
                    const m = TIER_META[tier];
                    return (
                      <button
                        type="button"
                        onClick={() => handleTierToggle(c)}
                        data-testid={`cust-tier-${c.id}`}
                        title={`اضغط للتبديل إلى ${tier === "A_plus" ? "B+ (زبون مميّز)" : "A+ (زبون عادي)"}`}
                        className="flex items-center gap-1 text-white text-xs font-black px-2.5 py-1.5 rounded-full transition-transform hover:scale-105 active:scale-95"
                        style={{ backgroundColor: m.color }}
                      >
                        {tier === "B_plus" && <Sparkles className="w-3 h-3" />}
                        {m.label}
                      </button>
                    );
                  })()}
                  <button
                    onClick={() => handleDelete(c)}
                    disabled={deletingId === c.id}
                    data-testid={`cust-delete-${c.id}`}
                    className="flex items-center gap-1 bg-[#E85D45] hover:bg-[#c14a36] text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 whitespace-nowrap"
                    title="حذف الزبون"
                  >
                    <Trash2 className="w-3 h-3" />
                    حذف
                  </button>
                </div>
              </div>
              {c.customer_id && (
                <p className="text-xs text-[#2D4A3E] mt-1.5 inline-flex items-center gap-1 bg-[#E0F2FE] px-2 py-0.5 rounded-full font-mono font-bold">
                  <IdCard className="w-3 h-3" />
                  ايباد: {c.customer_id}
                </p>
              )}
              <a href={`tel:${c.phone}`} className="text-xs text-[#E85D45] flex items-center gap-1 mt-1 hover:underline">
                <Phone className="w-3 h-3" />
                {c.phone}
              </a>
              {c.location && (
                <p className="text-xs text-[#5C6E64] mt-1">📍 {c.location}</p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <button
                  onClick={() => setLocOpen(c.id)}
                  data-testid={`reveal-loc-${c.id}`}
                  title="كشف موقع الزبون على الخريطة (كل المواقع المحفوظة)"
                  className="inline-flex items-center gap-1 bg-[#15803D] hover:bg-[#126b32] text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm transition-colors"
                >
                  <MapPin className="w-3 h-3" />
                  كشف الموقع
                </button>
                {/* Two-way accounting: mark customer as buyable-from supplier */}
                <button
                  onClick={() => handleBuyFromToggle(c)}
                  data-testid={`buy-from-${c.id}`}
                  title={c.can_buy_from
                    ? "مُفعّل — يظهر بقائمة المُجهّزين. اضغط للإيقاف."
                    : "فعّل هذا الخيار لتقدر تشتري بضاعة من عند هذا الزبون (حساب متبادل)."}
                  className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm transition-colors ${
                    c.can_buy_from
                      ? "bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                      : "bg-white border border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF]"
                  }`}
                >
                  <Store className="w-3 h-3" />
                  {c.can_buy_from ? "✓ يُشترى منه" : "شراء منه؟"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#F3F6F4] rounded-xl p-2.5">
                <p className="text-[10px] text-[#5C6E64]">مبيعات الشهر</p>
                <p className="font-bold text-sm text-[#2D4A3E]">{fmt(c.month_revenue)}</p>
                <p className="text-[10px] text-[#5C6E64]">{c.month_orders} طلب · {c.month_quantity} قطعة</p>
              </div>
              <div className="bg-[#F3F6F4] rounded-xl p-2.5">
                <p className="text-[10px] text-[#5C6E64]">إجمالي المبيعات</p>
                <p className="font-bold text-sm text-[#111814]">{fmt(c.total_revenue)}</p>
                <p className="text-[10px] text-[#5C6E64]">{c.total_orders} طلب · {c.total_quantity} قطعة</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#7ED321]/15 to-[#7ED321]/5 border border-[#7ED321]/30 rounded-xl px-3 py-2 flex justify-between items-center">
              <div>
                <p className="text-[10px] text-[#5C6E64]">رصيد المكافآت</p>
                <p className="font-bold text-lg text-[#2D4A3E]">{fmt(c.rewards_balance)} <span className="text-xs">د.ع</span></p>
              </div>
              {c.pending_withdrawal > 0 && (
                <span className="bg-[#F5A623] text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                  طلب سحب
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E4EBE6]">
          <Users className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا يوجد زبائن مطابقين</p>
        </div>
      )}
      {locOpen && (
        <CustomerLocationsModal customerId={locOpen} onClose={() => setLocOpen(null)} />
      )}
    </div>
  );
};

export default CustomersTab;
